/**
 * Markdown image matching that survives a real help center's filenames.
 *
 * HubSpot stores uploads as "image (88).png", so the destination regularly
 * carries parentheses. A `[^)]+` destination truncates that at the first `)`
 * and every consumer downstream then fetches a URL that 404s.
 */

// Per CommonMark a bare destination may contain parentheses as long as they
// balance, and an angle-bracket destination may contain almost anything.
const DESTINATION = "<[^<>\\n]*>|(?:[^()\\s\\\\]|\\\\.|\\([^()\\s]*\\))+"

// A title after the destination, which the rewriters carry through untouched.
const TITLE = "(?:[ \\t]+(?:\"[^\"]*\"|'[^']*'|\\([^()]*\\)))?"

export const MARKDOWN_IMAGE = new RegExp(
	`!\\[([^\\]]*)\\]\\((${DESTINATION})(${TITLE})\\)`,
	"g",
)

/** The same shape without capture groups, for stripping images out of text. */
export const MARKDOWN_IMAGE_BARE = new RegExp(
	`!\\[[^\\]]*\\]\\((?:${DESTINATION})(?:${TITLE})\\)`,
	"g",
)

/**
 * `<https://x/a b.png>` is a destination; the brackets are not part of the URL.
 * GitBook escapes `&` as `\&`, which showdown keeps, and `alt=media\` then
 * answers with the file's JSON metadata instead of the image.
 */
export function unwrapDestination(destination: string): string {
	const url =
		destination.startsWith("<") && destination.endsWith(">")
			? destination.slice(1, -1)
			: destination
	return url.replace(/\\([!-/:-@[-`{-~])/g, "$1")
}

/**
 * Wraps a destination in angle brackets when it carries a parenthesis, so the
 * markdown we write back is unambiguous whatever reads it next.
 */
export function wrapDestination(url: string): string {
	if (!/[()]/.test(url)) return url
	if (url.startsWith("<") && url.endsWith(">")) return url
	return `<${url}>`
}

/**
 * Showdown reads a bare destination up to the first `)`, so a HubSpot filename
 * like "header (600x200) (32).png" reaches the article cut in half and 404s.
 */
export function bracketImageDestinations(markdown: string): string {
	return markdown.replace(
		MARKDOWN_IMAGE,
		(_whole, alt: string, destination: string, rest: string) =>
			`![${alt}](${wrapDestination(unwrapDestination(destination))}${rest})`,
	)
}
